import { FlexRow } from '@/components/common/Flex';
import Icon from '@/components/common/icons/Icon';
import Modal from '@/components/common/modals/Modal';
import { ITEMS_PER_PAGE } from '@/components/goals/GoalActivitiesSection';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { useMutation } from '@apollo/client';
import { getNextTransactionDateForPlan } from '@common/atomic/helpers';
import {
    AllFinancialEntitiesDocument,
    DeleteTransactionScheduleDocument,
    GoalActivitiesDocument,
    TransactionScheduleFieldsFragment,
} from '@common/graphql/generated';
import { useIsGoalToGoalTransaction } from '@common/react/goals';
import { IconName } from '@common/react/icons';
import {
    TransactionProps,
    useIsEditingEnabled,
} from '@common/react/transactions';
import { useCurrentUser } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import { TransactionSchedulePlan } from '@common/types';
import { Button, ButtonProps } from '../common/Button';
import { FlexCol } from '../common/Flex';
import { showConfirmModal } from '../common/modals/ConfirmModal';
import { useToasts } from '../common/Toasts';
import { Text } from '../common/Typography';
import { showGoalDepositFlowModal } from './GoalDepositFlowModal';
import GoalTransactionStatusHeader from './GoalTransactionStatusHeader';
import GoalTransactionStatusView from './GoalTransactionStatusView';

export type GoalScheduledTransactionStatusModalProps = {
    sourceId: string;
    destinationId: string;
    transactionSchedule:
        | TransactionSchedulePlan
        | TransactionScheduleFieldsFragment;
    goalId: string;
};

export const showGoalScheduledTransactionStatusModal = (
    props: GoalScheduledTransactionStatusModalProps
) => ModalManager.show(GoalScheduledTransactionStatusModal, props);

const GoalScheduledTransactionStatusModal =
    ModalManager.create<GoalScheduledTransactionStatusModalProps>((props) => {
        useTrackModalShown('Goal Scheduled Transaction Status');

        const { sourceId, destinationId, transactionSchedule, goalId } = props;
        const modal = useModal();

        const nextTransactionDate =
            getNextTransactionDateForPlan(transactionSchedule);

        const transaction: TransactionProps = {
            sourceId,
            destinationId,
            amount: transactionSchedule.amount,
            plannedProcessingDate: nextTransactionDate.toDateStr(),
            status: 'SCHEDULED',
        };

        const isGoalToGoalTransaction = useIsGoalToGoalTransaction(transaction);
        const onClose = () => modal.remove();

        return (
            <Modal
                modalBodyProps={{ pt: 0 }}
                renderHeader={() => (
                    <GoalTransactionStatusHeader
                        transaction={transaction}
                        transactionSchedule={
                            'id' in transactionSchedule
                                ? transactionSchedule
                                : undefined
                        }
                        goalDirection={
                            isGoalToGoalTransaction ? 'GOAL_TO_GOAL' : 'DEPOSIT'
                        }
                        titleRight={
                            isTransactionSchedule(transactionSchedule) ? (
                                <TransactionScheduleControls
                                    sourceId={sourceId}
                                    goalId={goalId}
                                    destinationId={destinationId}
                                    transactionSchedule={transactionSchedule}
                                    onClose={onClose}
                                />
                            ) : (
                                <></>
                            )
                        }
                    />
                )}
                size="md"
                isOpen={modal.visible}
                onClose={onClose}
            >
                <GoalTransactionStatusView transaction={transaction} />
            </Modal>
        );
    });

function TransactionScheduleControls({
    sourceId,
    destinationId,
    transactionSchedule,
    goalId,
    onClose,
}: GoalScheduledTransactionStatusModalProps & {
    transactionSchedule: TransactionScheduleFieldsFragment;
    onClose: () => void;
}) {
    const { featureFlags } = useCurrentUser();
    const { showErrorToast, showSuccessToast } = useToasts();
    const [deleteTransactionSchedule] = useMutation(
        DeleteTransactionScheduleDocument,
        {
            refetchQueries: [
                { query: AllFinancialEntitiesDocument },
                {
                    query: GoalActivitiesDocument,
                    variables: {
                        goalId,
                        financialEntityFilter: [
                            { financialEntityId: { equalTo: sourceId } },
                            { financialEntityId: { equalTo: destinationId } },
                            { financialEntityId: { isNull: true } },
                        ],
                        itemsPerPage: ITEMS_PER_PAGE,
                    },
                },
            ],
        }
    );

    const isEditingEnabled = useIsEditingEnabled(transactionSchedule);
    if (!isEditingEnabled) return <></>;

    return (
        <FlexRow mb={-1.5}>
            <ActionIconWithTextButton
                iconName="StopSquare"
                text="Stop"
                onClick={() =>
                    showConfirmModal({
                        title: 'Are you sure you want to stop this recurring deposit?',
                        description:
                            'This will cancel all related upcoming deposits. Transfers that have already been processed will not be affected.',
                        buttonText: 'Stop recurring deposit',
                        onConfirm: async () => {
                            await deleteTransactionSchedule({
                                variables: {
                                    transactionScheduleId:
                                        transactionSchedule.id,
                                },
                                onCompleted: () => {
                                    showSuccessToast(
                                        TOASTS.deleteTransactionScheduleSuccess
                                    );
                                    onClose();
                                },
                                onError: () => {
                                    showErrorToast(
                                        TOASTS.deleteTransactionScheduleError
                                    );
                                },
                            });
                        },
                        onClickPrimaryEvent:
                            'Stop Transaction Schedule Confirm Button Clicked',
                        onClickSecondaryEvent:
                            'Cancel Stop Transaction Schedule Button Clicked',
                    })
                }
                onClickEvent="Stop Transaction Schedule Button Clicked"
            />
            {!featureFlags.isShutdownV1Enabled && (
                <ActionIconWithTextButton
                    iconName="Edit02"
                    text="Edit"
                    onClick={() => {
                        showGoalDepositFlowModal({
                            destinationId,
                            goalId,
                            existingTransactionSchedule: transactionSchedule,
                            onComplete: () => onClose(),
                        });
                    }}
                    onClickEvent="Edit Transaction Schedule Button Clicked"
                />
            )}
        </FlexRow>
    );
}

interface ActionIconWithTextButtonProps extends ButtonProps {
    iconName: IconName;
    text: string;
}

function ActionIconWithTextButton({
    iconName,
    text,
    ...props
}: ActionIconWithTextButtonProps) {
    return (
        <Button size="sm" variant="ghost" h={14} w={14} {...props}>
            <FlexCol align="center">
                <Icon name={iconName} size="md" mb={1} />
                <Text fontSize="md" fontWeight="normal">
                    {text}
                </Text>
            </FlexCol>
        </Button>
    );
}

const isTransactionSchedule = (
    ts: TransactionSchedulePlan | TransactionScheduleFieldsFragment
): ts is TransactionScheduleFieldsFragment => {
    return 'id' in ts;
};

export default GoalScheduledTransactionStatusModal;
